import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Card } from 'react-bootstrap';
import PositionSizeCalculator from '../components/PositionSizeCalculator';
import StopLossCalculator from '../components/StopLossCalculator';
import RiskRewardVisualizer from '../components/RiskRewardVisualizer';
import MarginPositionCalculator from '../components/MarginPositionCalculator';
import MarginPercentageCalculator from '../components/MarginPercentageCalculator';
import { RiskSettingsStorage } from '../utils/riskStorage';

const RiskManagement = () => {
  // Initialize with stored tab or default to position-size
  const [activeTab, setActiveTab] = useState(() => {
    return RiskSettingsStorage.loadActiveTab();
  });
  
  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    RiskSettingsStorage.saveActiveTab(activeTab);
  }, [activeTab]);
  
  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <h2 className="mb-3">
                <i className="bi bi-shield-check me-2"></i>
                Risk Management Tools
              </h2>
              <p className="text-muted mb-0">
                Effective risk management is the foundation of successful trading. Use these tools to
                calculate optimal position sizes, determine stop loss levels, and evaluate risk-to-reward ratios
                to protect your capital and maximize profits.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col>
          <Nav 
            variant="pills" 
            className="risk-management-tabs"
            activeKey={activeTab} 
            onSelect={(tab) => setActiveTab(tab)}
          >
            <Nav.Item>
              <Nav.Link 
                eventKey="position-size"
                className="d-flex align-items-center"
              >
                <i className="bi bi-calculator me-2"></i>
                Position Size
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="margin-position"
                className="d-flex align-items-center"
              >
                <i className="bi bi-calculator-fill me-2"></i>
                Leverage Calc
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="margin-percentage"
                className="d-flex align-items-center"
              >
                <i className="bi bi-percent me-2"></i>
                Margin % Calc
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="stop-loss"
                className="d-flex align-items-center"
              >
                <i className="bi bi-shield-x me-2"></i>
                Stop Loss
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                eventKey="risk-reward"
                className="d-flex align-items-center"
              >
                <i className="bi bi-bar-chart me-2"></i>
                Risk/Reward
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>
      
      {activeTab === 'position-size' && (
        <Row>
          <Col>
            <PositionSizeCalculator />
            
            <Card className="mb-4 info-card">
              <Card.Header className="card-header-gradient">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Position Sizing Guide
                </h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Why Position Sizing Matters</h6>
                <p>
                  Proper position sizing is one of the most important aspects of risk management in trading.
                  It determines how much of your capital you put at risk on each trade, directly impacting your:
                </p>
                <ul className="mb-4">
                  <li><strong>Survival:</strong> Preventing large drawdowns that could deplete your account</li>
                  <li><strong>Consistency:</strong> Maintaining a stable risk percentage across different trades</li>
                  <li><strong>Psychology:</strong> Trading with position sizes that don't cause emotional decisions</li>
                  <li><strong>Growth:</strong> Optimizing returns while controlling risk</li>
                </ul>
                
                <h6 className="mb-3">Position Sizing Best Practices</h6>
                <ul className="mb-0">
                  <li><strong>The 1% Rule:</strong> Many professional traders limit risk to 1-2% of their account per trade</li>
                  <li><strong>Kelly Criterion:</strong> Mathematically optimal sizing based on edge and win rate</li>
                  <li><strong>Fixed Dollar Risk:</strong> Useful for maintaining consistent absolute risk levels</li>
                  <li><strong>Scale in/out:</strong> Consider scaling positions for better risk management</li>
                  <li><strong>Account for Volatility:</strong> More volatile markets may require smaller position sizes</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {activeTab === 'margin-position' && (
        <Row>
          <Col>
            <MarginPositionCalculator />
            
            <Card className="mb-4 info-card">
              <Card.Header className="card-header-gradient">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Leveraged Trading Guide
                </h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Understanding Margin Trading</h6>
                <p>
                  Margin trading allows you to control a larger position with a smaller amount of capital, but comes with increased risk:
                </p>
                <ul className="mb-4">
                  <li><strong>Leverage:</strong> Amplifies both gains and losses by the leverage factor</li>
                  <li><strong>Liquidation Risk:</strong> If your position moves against you beyond a certain point, it may be automatically closed</li>
                  <li><strong>Margin Call:</strong> An alert that your position is approaching liquidation, requiring additional funds</li>
                  <li><strong>Funding Rates:</strong> Periodic payments between longs and shorts in perpetual contracts</li>
                </ul>
                
                <h6 className="mb-3">Margin Trading Best Practices</h6>
                <ul className="mb-0">
                  <li><strong>Lower Leverage:</strong> Use lower leverage (2-5x) for safer trading, especially for beginners</li>
                  <li><strong>Account for Liquidation:</strong> Ensure your stop loss is triggered before liquidation price</li>
                  <li><strong>Isolated Margin:</strong> Limits losses to the margin allocated to an individual position</li>
                  <li><strong>Cross Margin:</strong> Uses entire account balance to avoid liquidation, but risks more capital</li>
                  <li><strong>Reduce Position Size:</strong> When using leverage, consider reducing your position size further</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {activeTab === 'margin-percentage' && (
        <Row>
          <Col>
            <MarginPercentageCalculator />
            
            <Card className="mb-4 info-card">
              <Card.Header className="card-header-gradient">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Margin Percentage Guide
                </h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Margin Requirement vs. Leverage</h6>
                <p>
                  Margin requirement and leverage are inverse concepts:
                </p>
                <ul className="mb-4">
                  <li><strong>Margin Requirement:</strong> The percentage of position value required as collateral</li>
                  <li><strong>Leverage:</strong> The multiplier of your capital (Leverage = 100% ÷ Margin Requirement %)</li>
                  <li><strong>Examples:</strong>
                    <ul>
                      <li>5% margin requirement = 20x leverage (100% ÷ 5% = 20)</li>
                      <li>10% margin requirement = 10x leverage (100% ÷ 10% = 10)</li>
                      <li>50% margin requirement = 2x leverage (100% ÷ 50% = 2)</li>
                      <li>100% margin requirement = 1x leverage (no leverage)</li>
                    </ul>
                  </li>
                </ul>
                
                <h6 className="mb-3">Common Margin Requirements by Market</h6>
                <ul className="mb-0">
                  <li><strong>Stocks:</strong> 50% initial margin requirement (2x leverage) - regulated by Regulation T</li>
                  <li><strong>Futures:</strong> 3-12% margin requirements depending on contract (8-33x leverage)</li>
                  <li><strong>Forex:</strong> 2-10% margin requirements (10-50x leverage) depending on jurisdiction</li>
                  <li><strong>Crypto:</strong> 1-50% margin requirements (2-100x leverage) depending on platform</li>
                  <li><strong>Options:</strong> Varying requirements based on strategy, usually higher than stock</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {activeTab === 'stop-loss' && (
        <Row>
          <Col>
            <StopLossCalculator />
            
            <Card className="mb-4 info-card">
              <Card.Header className="card-header-gradient">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Stop Loss Placement Guide
                </h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Stop Loss Strategies</h6>
                <p>
                  A well-placed stop loss is critical for risk management. Here are the main approaches:
                </p>
                <ul className="mb-4">
                  <li>
                    <strong>Percentage-Based Stops:</strong> Simple but doesn't account for market conditions.
                    Typically 1-5% for stocks, 0.5-2% for forex, and 5-15% for cryptocurrencies.
                  </li>
                  <li>
                    <strong>Volatility-Based Stops (ATR):</strong> Adapts to market volatility. Wide enough to avoid
                    being hit by normal price fluctuations, yet close enough to limit losses.
                  </li>
                  <li>
                    <strong>Technical Stops:</strong> Based on support/resistance levels, trend lines, or other
                    technical indicators. Most effective but requires proper technical analysis.
                  </li>
                  <li>
                    <strong>Time-Based Stops:</strong> Exits a trade after a predetermined time period if it
                    hasn't performed as expected.
                  </li>
                </ul>
                
                <h6 className="mb-3">Common Stop Loss Mistakes</h6>
                <ul className="mb-0">
                  <li><strong>Too Tight:</strong> Stops placed too close get triggered by normal market noise</li>
                  <li><strong>Too Wide:</strong> Stops placed too far result in excessive losses when triggered</li>
                  <li><strong>Obvious Levels:</strong> Placing stops at obvious round numbers or common levels</li>
                  <li><strong>Moving Stops Wider:</strong> Extending stops to avoid taking a loss (breaking discipline)</li>
                  <li><strong>Ignoring Volatility:</strong> Using fixed percentages without considering market conditions</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      
      {activeTab === 'risk-reward' && (
        <Row>
          <Col>
            <RiskRewardVisualizer />
            
            <Card className="mb-4 info-card">
              <Card.Header className="card-header-gradient">
                <h5 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Understanding Risk/Reward
                </h5>
              </Card.Header>
              <Card.Body>
                <h6 className="mb-3">Risk/Reward Ratio Explained</h6>
                <p>
                  The risk/reward ratio compares your potential profit (reward) to your potential loss (risk) on a trade.
                  It's typically expressed as a ratio like 1:2, meaning the potential profit is twice the potential loss.
                </p>
                <ul className="mb-4">
                  <li><strong>Risk:</strong> The amount you could lose if your stop loss is hit</li>
                  <li><strong>Reward:</strong> The amount you could gain if your profit target is hit</li>
                  <li><strong>R-Multiple:</strong> Expressing gains/losses in terms of the initial risk (1R, 2R, etc.)</li>
                </ul>
                
                <h6 className="mb-3">Win Rate and Expectancy</h6>
                <p>
                  Risk/reward ratio alone isn't enough - it must be considered alongside win rate to determine profitability.
                </p>
                <ul className="mb-0">
                  <li><strong>Expected Value:</strong> (Win Rate × Reward) - (Loss Rate × Risk)</li>
                  <li><strong>Break-Even Win Rate:</strong> The minimum win rate needed for profitability</li>
                  <li><strong>Examples:</strong>
                    <ul>
                      <li>1:1 R/R requires &gt;50% win rate</li>
                      <li>1:2 R/R requires &gt;33% win rate</li>
                      <li>1:3 R/R requires &gt;25% win rate</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Trade Quality:</strong> Higher R/R trades (2:1 or better) generally allow for a lower
                    win rate and more sustainable profitability
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default RiskManagement;